import { logger } from "../utils/logger";
// Base API service for custom backend
export class ApiService {
  private static baseUrl =
    process.env.REACT_APP_API_BASE_URL ||
    (() => {
      throw new Error("REACT_APP_API_BASE_URL is required");
    })();
  private static token: string | null = null;
  private static refreshPromise: Promise<boolean> | null = null;
  private static logoutCallback: (() => void) | null = null;

  static setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  // Set authentication token
  static setAuthToken(token: string) {
    this.token = token;
  }

  // Remove authentication token
  static clearAuthToken() {
    this.token = null;
    this.refreshPromise = null;
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("remember_me");
    localStorage.removeItem("login_timestamp");
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("remember_me");
    sessionStorage.removeItem("login_timestamp");
  }

  // Get current token (fallback to localStorage if static token is null)
  static getAuthToken(): string | null {
    if (!this.token) {
      this.token =
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("auth_token");
    }
    return this.token;
  }

  // Get refresh token from storage
  private static getRefreshToken(): string | null {
    return (
      localStorage.getItem("refresh_token") ||
      sessionStorage.getItem("refresh_token")
    );
  }

  // Update tokens in storage
  private static updateTokens(accessToken: string, refreshToken: string): void {
    const rememberMe = localStorage.getItem("remember_me") === "true";
    const storage = rememberMe ? localStorage : sessionStorage;

    this.token = accessToken;
    storage.setItem("auth_token", accessToken);
    storage.setItem("refresh_token", refreshToken);
    storage.setItem("login_timestamp", Date.now().toString());

    // Clear from opposite storage
    const oppositeStorage = rememberMe ? sessionStorage : localStorage;
    oppositeStorage.removeItem("auth_token");
    oppositeStorage.removeItem("refresh_token");
  }

  // Refresh access token using refresh token
  private static async refreshToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();

      // Handle your backend's response format: nested in data object
      const tokenData = data.data || data;
      if (tokenData.token && tokenData.refresh_token) {
        this.updateTokens(tokenData.token, tokenData.refresh_token);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  // Base fetch method with authentication and auto-refresh
  private static async apiRequest(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(options.headers as Record<string, string>),
    };

    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;

    if (isFormData) {
      delete headers["Content-Type"];
      delete headers["content-type"];
    } else if (!("Content-Type" in headers) && !("content-type" in headers)) {
      headers["Content-Type"] = "application/json";
    }

    // List of auth/public paths that do not require token
    const publicPaths = [
      "/auth/login",
      "/auth/register",
      "/auth/validate-email",
      "/auth/request-password-reset",
      "/auth/verify-reset-code",
      "/auth/reset-password",
    ];
    const requiresToken = !publicPaths.includes(endpoint);

    if (requiresToken) {
      const token = this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      // Handle token expiration
      const shouldAttemptRefresh =
        (response.status === 401 || response.status === 403) &&
        !isRetry &&
        requiresToken &&
        endpoint !== "/auth/refresh";

      if (shouldAttemptRefresh) {
        // Prevent multiple simultaneous refresh attempts
        if (!this.refreshPromise) {
          this.refreshPromise = this.refreshToken();
        }

        const refreshSuccess = await this.refreshPromise;
        this.refreshPromise = null;

        if (refreshSuccess) {
          // Retry the original request with new token
          return this.apiRequest(endpoint, options, true);
        } else {
          // Refresh failed, clear tokens and logout
          this.clearAuthToken();

          if (this.logoutCallback) {
            this.logoutCallback();
          }

          throw new Error("Session expired. Please log in again.");
        }
      }

      return response;
    } catch (error) {
      logger.error("API request failed:", error);
      throw error;
    }
  }

  // GET request
  static async get(endpoint: string): Promise<Response> {
    return this.apiRequest(endpoint, {
      method: "GET",
    });
  }

  // POST request
  static async post(endpoint: string, data: any = null): Promise<Response> {
    const isFormData =
      typeof FormData !== "undefined" && data instanceof FormData;

    return this.apiRequest(endpoint, {
      method: "POST",
      body: isFormData ? data : data ? JSON.stringify(data) : null,
    });
  }

  // PUT request
  static async put(endpoint: string, data: any): Promise<Response> {
    const isFormData =
      typeof FormData !== "undefined" && data instanceof FormData;

    return this.apiRequest(endpoint, {
      method: "PUT",
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  // DELETE request
  static async delete(endpoint: string, data: any = null): Promise<Response> {
    return this.apiRequest(endpoint, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : null,
    });
  }

  // PATCH request
  static async patch(endpoint: string, data: any): Promise<Response> {
    const isFormData =
      typeof FormData !== "undefined" && data instanceof FormData;

    return this.apiRequest(endpoint, {
      method: "PATCH",
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  // One-step request + parse.
  //
  // The get/post/... methods return a raw Response so callers can inspect
  // status, but almost nobody needs that: ~110 call sites paired them with
  // handleResponse. These collapse the pair. Prefer them in new code; the
  // raw methods stay for the few places that do need the Response.
  static async getJson<T>(endpoint: string): Promise<T> {
    return this.handleResponse<T>(await this.get(endpoint));
  }

  static async postJson<T>(endpoint: string, data: any = null): Promise<T> {
    return this.handleResponse<T>(await this.post(endpoint, data));
  }

  static async putJson<T>(endpoint: string, data: any): Promise<T> {
    return this.handleResponse<T>(await this.put(endpoint, data));
  }

  static async patchJson<T>(endpoint: string, data: any): Promise<T> {
    return this.handleResponse<T>(await this.patch(endpoint, data));
  }

  static async deleteJson<T>(endpoint: string, data: any = null): Promise<T> {
    return this.handleResponse<T>(await this.delete(endpoint, data));
  }

  // Fetch a Blob (CSV export, file download). Goes through apiRequest, so
  // unlike a hand-rolled fetch it still gets the 401 refresh-and-retry.
  static async getBlob(endpoint: string): Promise<Blob> {
    const response = await this.get(endpoint);

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.json();
        message = body.message || body.error || message;
      } catch (e) {
        // non-JSON error body; keep the status message
      }
      throw new Error(message);
    }

    return response.blob();
  }

  // Handle API response and parse JSON
  static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If parsing error response fails, use default message
      }

      throw new Error(errorMessage);
    }

    try {
      return await response.json();
    } catch (error) {
      throw new Error("Failed to parse response JSON");
    }
  }
}
